/**
 * Formatage des valeurs renvoyées par l'API.
 *
 * Deux conventions à connaître, valables pour toute l'API Dash Lite :
 *
 * - les **montants sont en centimes**, accompagnés de leur devise ISO 4217 ;
 * - les **dates sont en ISO 8601 UTC** (`2026-08-06T15:30:00+00:00`).
 *
 * Les centraliser ici évite qu'un « /100 » oublié se glisse dans un écran.
 */

import type { Offer } from '@dash-lite/sdk';

/** La langue d'affichage du site. L'API, elle, est neutre. */
const LOCALE = 'fr-FR';

/**
 * Formate un montant renvoyé par l'API.
 *
 * @example formatMoney({ amount: 990, currency: 'EUR' }) // « 9,90 € »
 */
export function formatMoney(price: { amount: number; currency: string }): string {
    return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: price.currency,
    }).format(price.amount / 100);
}

/** Date seule, sans heure : « 6 août 2026 ». */
export function formatDate(iso: string | null | undefined): string {
    if (!iso) {
        return '—';
    }

    return new Intl.DateTimeFormat(LOCALE, { dateStyle: 'long' }).format(new Date(iso));
}

/** Date et heure : « 6 août 2026 à 15:30 ». */
export function formatDateTime(iso: string | null | undefined): string {
    if (!iso) {
        return '—';
    }

    return new Intl.DateTimeFormat(LOCALE, {
        dateStyle: 'long',
        timeStyle: 'short',
    }).format(new Date(iso));
}

/** Date au format court accepté par un `<input type="date">`. */
export function toDateInputValue(date: Date): string {
    return date.toISOString().slice(0, 10);
}

/**
 * Décrit une offre en une phrase, quel que soit son type.
 *
 * Les trois types d'offre partagent la même intégration technique, mais pas
 * la même promesse commerciale : cette fonction est le seul endroit du site
 * qui a besoin de les distinguer.
 */
export function describeOffer(offer: Offer): string {
    const price = formatMoney(offer.price);

    switch (offer.type) {
        case 'one_time':
            return offer.access_duration_hours
                ? `${price} — accès pendant ${formatDuration(offer.access_duration_hours)}`
                : `${price} — paiement unique`;

        case 'subscription':
            return `${price} ${offer.billing_interval === 'year' ? 'par an' : 'par mois'}`;

        case 'trial_then_subscription': {
            const trial = offer.trial;

            if (!trial) {
                return `${price} par ${offer.billing_interval === 'year' ? 'an' : 'mois'}`;
            }

            const trialDays = trial.days ? `${trial.days} jour${trial.days > 1 ? 's' : ''}` : "la période d'essai";

            return `${formatMoney(trial.price)} pendant ${trialDays}, puis ${price} par ${
                offer.billing_interval === 'year' ? 'an' : 'mois'
            }`;
        }
    }
}

/** Le tarif « principal » à afficher en gros sur une carte d'offre. */
export function headlinePrice(offer: Offer): string {
    if (offer.type === 'trial_then_subscription' && offer.trial) {
        return formatMoney(offer.trial.price);
    }

    return formatMoney(offer.price);
}

/** Convertit une durée d'accès en heures vers une formulation lisible. */
export function formatDuration(hours: number): string {
    if (hours % 24 === 0) {
        const days = hours / 24;

        if (days % 365 === 0) {
            const years = days / 365;

            return `${years} an${years > 1 ? 's' : ''}`;
        }

        if (days % 30 === 0) {
            const months = days / 30;

            return `${months} mois`;
        }

        return `${days} jour${days > 1 ? 's' : ''}`;
    }

    return `${hours} h`;
}

/**
 * Libellé d'un pays à partir de son code ISO 3166-1 alpha-2.
 * `Intl.DisplayNames` fait le travail sans table à maintenir.
 */
export function formatCountry(code: string | null | undefined): string {
    if (!code) {
        return '—';
    }

    return new Intl.DisplayNames([LOCALE], { type: 'region' }).of(code.toUpperCase()) ?? code;
}

/** Valeur affichable pour un champ du registre qui peut valoir `null`. */
export function orDash(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    return String(value);
}

/** « Oui » / « Non » / « — » pour les drapeaux booléens du registre. */
export function formatBoolean(value: boolean | null | undefined): string {
    if (value === null || value === undefined) {
        return '—';
    }

    return value ? 'Oui' : 'Non';
}
